/**
 * MaintenanceBanner — non-blocking pre-lockout warning strip that
 * shows during the `scheduled` phase (is_deactivated=true, now <
 * start_at). Users see it capped at 15 min before start_at
 * (WARNING_LEAD_MIN). Empty otherwise.
 *
 * 2026-09-27 (Dave)
 *   Sits above the TopBar as a full-width coral strip. Users can
 *   dismiss it per-session (localStorage flag scoped to updated_at
 *   so a new schedule re-summons the banner).
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const WARNING_LEAD_MIN = 15;
const _DISMISS_KEY_PREFIX = "ureserve.maint.dismissed:";

function _fmtStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch { return iso; }
}
function _minsUntil(iso, serverNowIso) {
  if (!iso) return 0;
  const target = new Date(iso).getTime();
  const now = serverNowIso ? new Date(serverNowIso).getTime() : Date.now();
  return Math.max(0, Math.round((target - now) / 60000));
}

export default function MaintenanceBanner({ platformState }) {
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissal whenever a new schedule lands (different updated_at).
  const dismissKey =
    platformState?.updated_at
      ? `${_DISMISS_KEY_PREFIX}${platformState.updated_at}`
      : null;
  useEffect(() => {
    if (!dismissKey) { setDismissed(false); return; }
    setDismissed(Boolean(localStorage.getItem(dismissKey)));
  }, [dismissKey]);

  if (!platformState || platformState.phase !== "scheduled") return null;

  const minsLeft = _minsUntil(platformState.start_at, platformState.server_now);
  if (minsLeft > WARNING_LEAD_MIN) return null;
  if (dismissed) return null;

  const dismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="maintenance-banner"
      className="w-full flex items-center gap-3 px-4 py-2"
      style={{
        background: "#EA6A47",
        color: "#fff",
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.1,
      }}
    >
      <AlertTriangle size={16} strokeWidth={2.4} />
      <div className="flex-1">
        Scheduled maintenance in{" "}
        <span className="font-mono tabular-nums font-bold">
          {minsLeft} min
        </span>{" "}
        · uReserve goes offline at{" "}
        <span className="font-mono tabular-nums">
          {_fmtStamp(platformState.start_at)}
        </span>{" "}
        and returns at{" "}
        <span className="font-mono tabular-nums">
          {_fmtStamp(platformState.back_at)}
        </span>
        . Please save your work and sign out.
        {platformState.note ? (
          <span className="opacity-90"> — {platformState.note}</span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="rounded p-1 hover:bg-white/15"
        aria-label="Dismiss banner"
        data-testid="maintenance-banner-dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}
